import { ColorV2 } from "config/colors";
import type { StaffRegisterStatus } from "services/api/company/timeReports.service";

export type StaffRegisterStatusTone =
  | "success"
  | "warning"
  | "info"
  | "danger"
  | "neutral";

/** Map a staff register status to the UI tone used by the status pill. */
export const toStaffRegisterStatusTone = (
  status: StaffRegisterStatus,
): StaffRegisterStatusTone => {
  if (status === "NoStampOut") return "danger";
  if (status === "TimeReported") return "info";
  if (status === "Stamped") return "success";
  if (status === "Modified") return "warning";
  if (status === "Started") return "success";
  if (status === "New") return "warning";
  return "neutral";
};

/** Map a staff register status to the optional icon shown in the status pill. */
export const toStaffRegisterStatusIconName = (
  status: StaffRegisterStatus,
): string | null => {
  if (status === "NoStampOut") return "v2/warning-triangle";
  if (status === "TimeReported") return "v2/document";
  if (status === "Stamped") return "v2/checkmark";
  if (status === "Modified") return "v2/pen";
  if (status === "Started") return "v2/checkmark";
  if (status === "New") return "v2/clock";
  return null;
};

/** Map a staff register status to icon color aligned with time-reports tones. */
export const toStaffRegisterStatusIconColor = (
  status: StaffRegisterStatus,
): ColorV2 => {
  const tone = toStaffRegisterStatusTone(status);
  if (tone === "danger") return ColorV2.NegativeRed;
  if (tone === "warning") return ColorV2.DarkAmbigiousYellow;
  if (tone === "info") return ColorV2.ElectricBlue;
  if (tone === "success") return ColorV2.PositiveGreen;
  return ColorV2.Blue2;
};

/** Map a staff register status to a translated user-facing status label. */
export const toStaffRegisterStatusText = (
  status: StaffRegisterStatus,
  translate: (key: string) => string,
): string => {
  switch (status) {
    case "Stamped":
      return translate("TimeReports.Stamped");
    case "TimeReported":
      return translate("TimeReports.TimeReported");
    case "Modified":
      return translate("TimeReports.Modified");
    case "NoStampOut":
      return translate("TimeReports.NoStampOut");
    case "Started":
      return translate("TimeReports.OnSite");
    case "New":
      return translate("TimeReports.NotCheckedIn");
    default: {
      const exhaustiveCheck: never = status;
      return exhaustiveCheck;
    }
  }
};
